import { Injectable, Injector, inject, runInInjectionContext } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import type { Observable } from 'rxjs';
import { FormModalComponent } from './form-modal.component';
import type { FormDefinition } from './form.types';

const DEFAULTS: Omit<MatDialogConfig, 'data'> = {
  width: '1200px',
  maxWidth: 'min(1200px, 90vw)',
  maxHeight: '95vh',
  autoFocus: 'first-tabbable',
  restoreFocus: true,
  panelClass: 'mty-form-dialog-panel',
};

/**
 * Opens a FormDefinition as a Material dialog.
 *
 *   formDialog.open(() => buildJobPostingDefinition(input))
 *     .subscribe(model => { if (model) saveJobPosting(...) });
 *
 * The builder runs inside an injection context so `inject(Service)` at the top
 * level of the builder works — callers don't need `runInInjectionContext`.
 * Emits the submitted model on Save, `undefined` on Cancel / backdrop / Esc.
 */
@Injectable({ providedIn: 'root' })
export class FormDialogService {
  private readonly dialog = inject(MatDialog);
  private readonly injector = inject(Injector);

  open<TModel>(
    builder: () => FormDefinition<TModel>,
    options?: Partial<MatDialogConfig>,
  ): Observable<TModel | undefined> {
    const def = runInInjectionContext(this.injector, builder);
    const ref = this.dialog.open<FormModalComponent<TModel>, FormDefinition<TModel>, TModel>(
      FormModalComponent,
      { ...DEFAULTS, ...options, data: def },
    );
    return ref.afterClosed();
  }
}
